import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink } from 'react-router-dom';
import { cn } from '@/lib/cn';
import {
  LayoutGrid, Check, Users, ShieldCheck, Lock, FileText, CreditCard, RotateCcw,
  Landmark, Star, Settings, TrendingUp, MessageSquare, ScrollText, PanelLeft, X,
  Shield,
} from 'lucide-react';
import { useMe } from '@/hooks/use-me';
import { useAuthStore } from '@shared/stores/auth-store';
import { useAdminVerificationUsers } from '@/hooks/use-admin';
import { useMyPermissions } from '@/hooks/use-admin-permissions';
import type { AdminRole, AdminSection } from '@shared/types/api';

const ADMIN_ROLE_LABELS: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'OH / Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Controlling Executive',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Customer Support Executive',
  FINANCE_EXECUTIVE: 'Finance Executive',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance Reviewer',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

const ADMIN_ROLE_BADGES: Record<AdminRole, string> = {
  SUPER_ADMIN: 'Super Admin',
  OH_SERVICE_MANAGER: 'Service Manager',
  ASSIGNER: 'Assigner',
  CASE_EXECUTIVE: 'Case Executive',
  ASSOCIATE_CONTROLLING_EXECUTIVE: 'Associate Exec',
  CUSTOMER_SUPPORT_EXECUTIVE: 'Support Exec',
  FINANCE_EXECUTIVE: 'Finance Exec',
  AUDITOR_COMPLIANCE_REVIEWER: 'Auditor / Compliance',
  OPS_ADMIN: 'Ops Admin',
  READ_ONLY_ADMIN: 'Read Only Admin',
  ASSIGNER_ADMIN: 'Assigner Admin',
};

type NavItem = {
  to: string;
  label: string;
  section?: AdminSection;
  icon: React.ReactNode;
  superAdminOnly?: boolean;
};

const NAV: NavItem[] = [
  {
    to: '/transactions',
    label: 'Transactions',
    section: 'TRANSACTIONS',
    icon: <LayoutGrid className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/kyc',
    label: 'KYC',
    section: 'KYC',
    icon: <Check className="w-[18px] h-[18px]" strokeWidth={2} />,
  },
  {
    to: '/users',
    label: 'Users',
    section: 'USERS',
    icon: <Users className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/admin-verification',
    label: 'Admin Verification',
    superAdminOnly: true,
    icon: <ShieldCheck className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/role-permissions',
    label: 'Role Permissions',
    superAdminOnly: true,
    icon: <Lock className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/invoices',
    label: 'Invoices',
    section: 'INVOICES',
    icon: <FileText className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/payouts',
    label: 'Payouts',
    section: 'PAYOUTS',
    icon: <CreditCard className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/refunds',
    label: 'Refunds',
    section: 'REFUNDS',
    icon: <RotateCcw className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/authorities',
    label: 'Authority',
    section: 'AUTHORITIES',
    icon: <Landmark className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/services',
    label: 'Services',
    section: 'SERVICES',
    icon: <Star className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/config',
    label: 'Config',
    section: 'CONFIG',
    icon: <Settings className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/reports',
    label: 'Reports',
    section: 'REPORTS',
    icon: <TrendingUp className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/feedbacks',
    label: 'Feedbacks',
    section: 'FEEDBACKS',
    icon: <MessageSquare className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
  {
    to: '/audit-logs',
    label: 'Audit Log',
    section: 'AUDIT_LOGS',
    icon: <ScrollText className="w-[18px] h-[18px]" strokeWidth={1.75} />,
  },
];

export function AdminSidebar({
  isOpen,
  onClose,
  showNav,
  isCollapsed,
  onToggleCollapse,
}: {
  isOpen: boolean;
  onClose: () => void;
  showNav?: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const [hoveredNav, setHoveredNav] = useState<{
    label: string;
    badge?: number;
    rect: DOMRect;
  } | null>(null);

  useEffect(() => {
    const handleClose = () => setHoveredNav(null);
    window.addEventListener('resize', handleClose);
    window.addEventListener('scroll', handleClose, true);
    return () => {
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('scroll', handleClose, true);
    };
  }, []);

  useEffect(() => {
    if (!isCollapsed) setHoveredNav(null);
  }, [isCollapsed]);

  const { data: me } = useMe();
  const session = useAuthStore((s) => s.session);
  const { data: myPermissions = [] } = useMyPermissions();

  const currentAdminRole = me?.adminRole || session?.adminRole;
  const isSuperAdmin = currentAdminRole === 'SUPER_ADMIN';

  const { data: pendingUsers } = useAdminVerificationUsers('PENDING_ADMIN_APPROVAL', {
    enabled: Boolean(isSuperAdmin && showNav),
  });
  const pendingCount = pendingUsers?.length ?? 0;

  const visibleNav = NAV.filter((item) => {
    if (isSuperAdmin) return true;
    if (item.superAdminOnly) return false;
    if (item.section) {
      return myPermissions.some((p) => p.section === item.section && p.canView);
    }
    return false;
  });

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 transition-opacity md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        onScroll={() => setHoveredNav(null)}
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-brand-900/30 bg-gradient-to-b from-brand-800 to-teal-900 text-white transition-all duration-300 transform md:sticky md:top-0 md:h-screen md:translate-x-0 md:flex select-none overflow-y-auto overflow-x-hidden overscroll-contain',
          isOpen ? 'translate-x-0' : '-translate-x-full',
          isCollapsed ? 'w-72 md:w-[68px]' : 'w-72',
        )}
      >
        {/* Expanded Header (always on mobile, on desktop when not collapsed) */}
        <div
          className={cn(
            'flex items-center justify-between px-3.5 py-4 border-b border-white/10 min-h-[64px]',
            isCollapsed && 'md:hidden',
          )}
        >
          <Link to="/" className="flex items-center gap-2.5 min-w-0" onClick={onClose}>
            <img
              src="/Ecregi-logo.png"
              alt="EcRegi"
              className="h-10 w-10 object-contain drop-shadow-sm shrink-0"
            />
            <span className="text-base font-bold text-white tracking-wide shrink-0">ECREGI</span>
            <span
              className="inline-flex min-w-0 items-center gap-1 rounded bg-white/20 text-white border border-white/30 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-tight whitespace-nowrap ml-1"
              title={currentAdminRole ? (ADMIN_ROLE_LABELS[currentAdminRole] ?? currentAdminRole) : 'Admin'}
            >
              <Shield className="h-2.5 w-2.5 shrink-0 text-emerald-300" strokeWidth={2.5} />
              <span className="truncate">
                {currentAdminRole
                  ? (ADMIN_ROLE_BADGES[currentAdminRole] ?? ADMIN_ROLE_LABELS[currentAdminRole] ?? currentAdminRole)
                  : 'Admin'}
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-1 shrink-0">
            {/* Desktop toggle collapse icon (ChatGPT style panel toggle) */}
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden md:inline-flex items-center justify-center rounded-lg p-1.5 text-white/80 hover:bg-white/10 hover:text-white transition-colors"
              aria-label="Close sidebar"
            >
              <PanelLeft className="w-[18px] h-[18px]" strokeWidth={1.75} />
            </button>
            {/* Mobile close button */}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1.5 text-white/80 hover:bg-white/10 md:hidden shrink-0"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" strokeWidth={2} />
            </button>
          </div>
        </div>

        {/* Collapsed Header: EcRegi logo shown by default; on hover transitions into toggle open icon */}
        {isCollapsed && (
          <div className="hidden md:flex flex-col items-center justify-center py-3.5 border-b border-white/10 min-h-[64px]">
            <button
              type="button"
              onClick={onToggleCollapse}
              onMouseEnter={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredNav({
                  label: 'Open sidebar',
                  rect,
                });
              }}
              onMouseLeave={() => setHoveredNav(null)}
              className="group relative flex h-11 w-11 items-center justify-center rounded-xl hover:bg-white/10 transition-all duration-200"
              aria-label="Open sidebar"
            >
              {/* Default: EcRegi logo (large, prominent, exact logo without white box) */}
              <img
                src="/Ecregi-logo.png"
                alt="EcRegi"
                className="h-10 w-10 object-contain drop-shadow-sm transition-all duration-200 group-hover:opacity-0 group-hover:scale-75"
              />

              {/* On hover: Toggle sidebar open icon */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200 rounded-xl bg-white/15">
                <PanelLeft className="w-5 h-5 text-white drop-shadow-xs" strokeWidth={1.75} />
              </div>
            </button>
          </div>
        )}

        <nav
          className={cn(
            'mt-5 flex flex-col gap-2.5 pb-6 transition-all duration-300',
            isCollapsed ? 'px-3.5 md:px-0 md:items-center' : 'px-3.5',
          )}
        >
          {showNav &&
            visibleNav.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => {
                  setHoveredNav(null);
                  onClose();
                }}
                onMouseEnter={(e) => {
                  if (!isCollapsed) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  setHoveredNav({
                    label: item.label,
                    badge: item.to === '/admin-verification' && pendingCount > 0 ? pendingCount : undefined,
                    rect,
                  });
                }}
                onMouseLeave={() => setHoveredNav(null)}
                className={({ isActive }) =>
                  cn(
                    'relative group flex items-center transition-all duration-200',
                    isCollapsed
                      ? 'gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold md:w-9 md:h-9 md:p-0 md:justify-center md:rounded-lg'
                      : 'gap-3 px-3.5 py-2.5 rounded-xl text-sm font-semibold',
                    isActive
                      ? 'bg-white text-brand-900 shadow-sm font-extrabold'
                      : 'text-brand-100 hover:bg-white/10 hover:text-white',
                  )
                }
              >
                <span className="shrink-0 flex items-center justify-center w-5 h-5">
                  {item.icon}
                </span>

                <span
                  className={cn(
                    'flex-1 truncate transition-opacity duration-200',
                    isCollapsed && 'md:hidden',
                  )}
                >
                  {item.label}
                </span>

                {item.to === '/admin-verification' && pendingCount > 0 && (
                  isCollapsed ? (
                    <>
                      {/* Notification dot in collapsed mode on desktop */}
                      <span className="hidden md:block absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-brand-800" />
                      {/* Full badge on mobile */}
                      <span className="md:hidden rounded-full bg-amber-400 text-brand-950 px-2 py-0.5 text-[10px] font-bold">
                        {pendingCount}
                      </span>
                    </>
                  ) : (
                    <span className="rounded-full bg-amber-400 text-brand-950 px-2 py-0.5 text-[10px] font-bold">
                      {pendingCount}
                    </span>
                  )
                )}
              </NavLink>
            ))}
        </nav>
      </aside>

      {/* Floating Tooltip Portal for collapsed sidebar (high-contrast dark tooltip for clear visibility against white pages) */}
      {isCollapsed && hoveredNav && typeof document !== 'undefined' && createPortal(
        <div
          style={{
            top: hoveredNav.rect.top + hoveredNav.rect.height / 2,
            left: hoveredNav.rect.right + 10,
          }}
          className="fixed z-[9999] -translate-y-1/2 pointer-events-none flex items-center select-none filter drop-shadow-lg transition-opacity duration-150"
        >
          {/* Left-pointing arrow pointer notch */}
          <svg
            className="absolute -left-2 top-1/2 -translate-y-1/2 text-gray-900"
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M0 6L8 0V12L0 6Z" />
          </svg>

          {/* Tooltip content bubble */}
          <div className="relative flex items-center gap-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap shadow-2xl border border-gray-750/70 border-white/10">
            <span>{hoveredNav.label}</span>
            {hoveredNav.badge !== undefined && hoveredNav.badge > 0 && (
              <span className="rounded-full bg-amber-400 text-brand-950 px-1.5 py-0.5 text-[10px] font-bold">
                {hoveredNav.badge}
              </span>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

